/**
 * AdminEvents — CRUD table for landing-page events plus a per-event
 * country breakdown of RSVPs.
 */
import React, { useContext, useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../../App';
import {
  Calendar, Plus, Edit, Trash2, Save, X, ArrowLeft, BarChart3, Globe,
  Eye, EyeOff, Loader2,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import ThemeToggle from '../../components/ThemeToggle';
import ToolSelector from '../../components/ToolSelector';
import BrandMark from '../../components/BrandMark';
import PageHero from '../../components/PageHero';

const emptyDraft = {
  kind: 'webinar', date_label: '', title: '', body: '',
  cta_label: 'Reserve seat', cta_link: '/login?mode=signup',
  is_active: true, sort_order: 0,
  is_ticketed: false, price_cents: 0, capacity: null, bundle_items: [],
};

const AdminEvents = () => {
  const { API } = useContext(AuthContext);
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [draft, setDraft] = useState(emptyDraft);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [statsOpen, setStatsOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/events`);
      setItems(data?.items || []);
    } catch (_) { toast.error('Could not load events'); }
    setLoading(false);
  }, [API]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const openCreate = () => { setDraft(emptyDraft); setEditingId(null); setEditorOpen(true); };
  const openEdit = (ev) => { setDraft({ ...ev }); setEditingId(ev.id); setEditorOpen(true); };

  const save = async () => {
    if (!draft.title.trim() || !draft.date_label.trim()) { toast.error('Title and date label are required'); return; }
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/admin/events/${editingId}`, draft);
        toast.success('Event updated');
      } else {
        await axios.post(`${API}/admin/events`, draft);
        toast.success('Event created');
      }
      setEditorOpen(false);
      await fetchItems();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  const remove = async (ev) => {
    if (!window.confirm(`Delete "${ev.title}"?`)) return;
    try {
      await axios.delete(`${API}/admin/events/${ev.id}`);
      toast.success('Deleted');
      await fetchItems();
    } catch (_) { toast.error('Delete failed'); }
  };

  const toggleActive = async (ev) => {
    try {
      await axios.put(`${API}/admin/events/${ev.id}`, { is_active: !ev.is_active });
      await fetchItems();
    } catch (_) { toast.error('Update failed'); }
  };

  const openStats = async (ev) => {
    setStatsOpen(true); setStatsLoading(true); setStats({ event: ev });
    try {
      const { data } = await axios.get(`${API}/admin/events/${ev.id}/rsvps`);
      setStats({ event: ev, ...data });
    } catch (_) { toast.error('Stats load failed'); }
    setStatsLoading(false);
  };

  return (
    <div className="min-h-screen bg-background" data-testid="admin-events-page">
      <header className="sticky top-0 z-30 border-b border-border bg-background/95 backdrop-blur">
        <div className="container mx-auto px-6 h-16 flex items-center justify-between">
          <button onClick={() => navigate('/admin/portal')} className="inline-flex items-center gap-1.5 text-sm font-semibold text-foreground/80 hover:text-foreground">
            <ArrowLeft className="w-4 h-4" /> Admin
          </button>
          <BrandMark size="md" onClick={() => navigate('/dashboard')} />
          <ThemeToggle />
        </div>
      </header>
      <ToolSelector />
      <PageHero
        eyebrow="Admin"
        eyebrowIcon={Calendar}
        title={<>Landing-page <span className="text-amber-brand">events</span></>}
        description="Create, edit and archive the events shown on the public landing page. Track RSVPs and country breakdown per event."
        backTo="/admin/portal" backLabel="Admin" testId="events-admin-hero"
        actions={
          <Button size="lg" onClick={openCreate} className="rounded-full font-extrabold px-6" data-testid="new-event-btn">
            <Plus className="w-4 h-4 mr-2" /> New event
          </Button>
        }
      />

      <main className="container mx-auto px-6 py-8 max-w-6xl">
        <div className="pro-card p-0 overflow-hidden">
          {loading && <div className="p-10 text-center text-sm text-muted-foreground">Loading…</div>}
          {!loading && items.length === 0 && (
            <div className="p-14 text-center" data-testid="events-empty">
              <div className="h-12 w-12 rounded-full bg-muted mx-auto flex items-center justify-center">
                <Calendar className="w-5 h-5 text-muted-foreground" />
              </div>
              <div className="mt-3 font-heading text-base font-extrabold">No events yet</div>
              <div className="text-sm text-muted-foreground mt-1">Create the first one — it shows up on the landing page immediately.</div>
              <Button onClick={openCreate} className="mt-4 rounded-full font-semibold" data-testid="events-empty-cta">
                <Plus className="w-4 h-4 mr-2" /> New event
              </Button>
            </div>
          )}
          {!loading && items.length > 0 && (
            <table className="w-full text-sm" data-testid="events-table">
              <thead className="bg-muted/40 text-xs uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="text-left px-4 py-3">Date</th>
                  <th className="text-left px-4 py-3">Event</th>
                  <th className="text-left px-4 py-3">Kind</th>
                  <th className="text-right px-4 py-3">RSVPs</th>
                  <th className="text-center px-4 py-3">Active</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {items.map((ev) => (
                  <tr key={ev.id} className="hover:bg-muted/20" data-testid={`event-row-${ev.id}`}>
                    <td className="px-4 py-3 font-heading font-extrabold whitespace-nowrap">{ev.date_label}</td>
                    <td className="px-4 py-3">
                      <div className="font-heading font-extrabold">{ev.title}</div>
                      <div className="text-xs text-muted-foreground line-clamp-1">{ev.body}</div>
                    </td>
                    <td className="px-4 py-3 text-xs uppercase tracking-widest text-muted-foreground">{ev.kind}</td>
                    <td className="px-4 py-3 text-right font-heading font-extrabold tabular-nums">
                      <button onClick={() => openStats(ev)} className="hover:text-primary" data-testid={`event-stats-${ev.id}`}>
                        {ev.rsvp_count || 0}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => toggleActive(ev)}
                        className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-widest ${ev.is_active ? 'border-primary text-primary' : 'border-border text-muted-foreground'}`}
                        data-testid={`event-toggle-${ev.id}`}
                      >
                        {ev.is_active ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                        {ev.is_active ? 'Live' : 'Hidden'}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => openEdit(ev)} className="text-muted-foreground hover:text-foreground p-1.5 rounded-md" data-testid={`event-edit-${ev.id}`} aria-label="Edit">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button onClick={() => remove(ev)} className="text-muted-foreground hover:text-destructive p-1.5 rounded-md ml-1" data-testid={`event-delete-${ev.id}`} aria-label="Delete">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>

      {/* Editor dialog */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-2xl" data-testid="event-editor-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit event' : 'New event'}</DialogTitle>
            <DialogDescription>All fields except sort order are visible on the landing page.</DialogDescription>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4 mt-2">
            <div className="space-y-1.5">
              <Label>Date label <span className="text-destructive">*</span></Label>
              <Input value={draft.date_label} onChange={(e) => setDraft({ ...draft, date_label: e.target.value })} placeholder="e.g. Feb 22" data-testid="event-date-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Kind</Label>
              <Input value={draft.kind} onChange={(e) => setDraft({ ...draft, kind: e.target.value })} placeholder="webinar / course drop / community" data-testid="event-kind-input" />
            </div>
            <div className="sm:col-span-2 space-y-1.5">
              <Label>Title <span className="text-destructive">*</span></Label>
              <Input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} placeholder="e.g. Cycle tuning for meats" data-testid="event-title-input" />
            </div>
            <div className="sm:col-span-2 space-y-1.5">
              <Label>Body</Label>
              <Textarea value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} rows={3} placeholder="Short description shown under the title." data-testid="event-body-input" />
            </div>
            <div className="space-y-1.5">
              <Label>CTA label</Label>
              <Input value={draft.cta_label} onChange={(e) => setDraft({ ...draft, cta_label: e.target.value })} data-testid="event-cta-label-input" />
            </div>
            <div className="space-y-1.5">
              <Label>CTA link</Label>
              <Input value={draft.cta_link} onChange={(e) => setDraft({ ...draft, cta_link: e.target.value })} placeholder="/login?mode=signup" data-testid="event-cta-link-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Sort order</Label>
              <Input type="number" value={draft.sort_order} onChange={(e) => setDraft({ ...draft, sort_order: parseInt(e.target.value || '0', 10) })} data-testid="event-sort-input" />
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={draft.is_active} onCheckedChange={(v) => setDraft({ ...draft, is_active: v })} data-testid="event-active-switch" />
              <Label>Visible on landing page</Label>
            </div>

            <div className="sm:col-span-2 border-t border-border pt-4 mt-2">
              <div className="flex items-center gap-3">
                <Switch checked={!!draft.is_ticketed} onCheckedChange={(v) => setDraft({ ...draft, is_ticketed: v })} data-testid="event-ticketed-switch" />
                <Label className="font-extrabold">Ticketed (paid event)</Label>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                When ON, users see a &ldquo;Buy ticket&rdquo; button on the shop instead of the free RSVP flow.
              </p>
            </div>

            {draft.is_ticketed && (
              <>
                <div className="space-y-1.5">
                  <Label>Ticket price (USD)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={(draft.price_cents || 0) / 100}
                    onChange={(e) => setDraft({ ...draft, price_cents: Math.round(parseFloat(e.target.value || '0') * 100) })}
                    data-testid="event-price-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Capacity (leave blank = unlimited)</Label>
                  <Input
                    type="number"
                    min="1"
                    value={draft.capacity ?? ''}
                    onChange={(e) => setDraft({ ...draft, capacity: e.target.value === '' ? null : parseInt(e.target.value, 10) })}
                    placeholder="e.g. 50"
                    data-testid="event-capacity-input"
                  />
                </div>
                <div className="sm:col-span-2 space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Bundle add-ons</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setDraft({
                        ...draft,
                        bundle_items: [...(draft.bundle_items || []), { label: '', description: '', price_cents: 0 }],
                      })}
                      className="rounded-full font-bold"
                      data-testid="event-bundle-add-btn"
                    >
                      + Add bundle item
                    </Button>
                  </div>
                  {(draft.bundle_items || []).map((b, idx) => (
                    <div key={idx} className="pro-card p-3 grid sm:grid-cols-[1fr_1fr_120px_auto] gap-2 items-end" data-testid={`event-bundle-row-${idx}`}>
                      <div className="space-y-1">
                        <Label className="text-xs">Label</Label>
                        <Input
                          value={b.label}
                          onChange={(e) => {
                            const next = [...draft.bundle_items];
                            next[idx] = { ...next[idx], label: e.target.value };
                            setDraft({ ...draft, bundle_items: next });
                          }}
                          placeholder="Recipe pack"
                          data-testid={`event-bundle-label-${idx}`}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Description</Label>
                        <Input
                          value={b.description || ''}
                          onChange={(e) => {
                            const next = [...draft.bundle_items];
                            next[idx] = { ...next[idx], description: e.target.value };
                            setDraft({ ...draft, bundle_items: next });
                          }}
                          placeholder="PDF + spreadsheet"
                          data-testid={`event-bundle-desc-${idx}`}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Add-on ($ USD)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={(b.price_cents || 0) / 100}
                          onChange={(e) => {
                            const next = [...draft.bundle_items];
                            next[idx] = { ...next[idx], price_cents: Math.round(parseFloat(e.target.value || '0') * 100) };
                            setDraft({ ...draft, bundle_items: next });
                          }}
                          data-testid={`event-bundle-price-${idx}`}
                        />
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setDraft({ ...draft, bundle_items: draft.bundle_items.filter((_, i) => i !== idx) })}
                        className="text-destructive hover:bg-destructive/10 rounded-full"
                        data-testid={`event-bundle-remove-${idx}`}
                      >
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
          <div className="mt-5 flex items-center justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setEditorOpen(false)} className="rounded-full font-semibold">
              <X className="w-4 h-4 mr-1" /> Cancel
            </Button>
            <Button onClick={save} disabled={saving} className="rounded-full font-extrabold" data-testid="event-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {editingId ? 'Save changes' : 'Create event'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Stats dialog */}
      <Dialog open={statsOpen} onOpenChange={setStatsOpen}>
        <DialogContent className="max-w-lg" data-testid="event-stats-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-primary" /> RSVP breakdown
            </DialogTitle>
            <DialogDescription>{stats?.event?.title}</DialogDescription>
          </DialogHeader>
          {statsLoading && <div className="text-center py-6 text-sm text-muted-foreground">Loading…</div>}
          {!statsLoading && stats && (
            <div>
              <div className="rounded-xl bg-primary/10 border border-primary/30 p-4 flex items-center gap-3">
                <div className="h-11 w-11 rounded-lg bg-primary text-primary-foreground flex items-center justify-center">
                  <Globe className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-2xl font-heading font-extrabold tabular-nums">{stats.total || 0}</div>
                  <div className="text-xs text-muted-foreground">total RSVPs</div>
                </div>
              </div>
              <div className="mt-4 space-y-2 max-h-72 overflow-y-auto">
                {Object.entries(stats.by_country || {}).sort((a, b) => b[1] - a[1]).map(([c, n]) => (
                  <div key={c} className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
                    <span className="font-heading font-extrabold text-sm">{c}</span>
                    <span className="text-sm tabular-nums text-muted-foreground">{n} RSVPs</span>
                  </div>
                ))}
                {Object.keys(stats.by_country || {}).length === 0 && (
                  <div className="text-center text-xs text-muted-foreground py-6">No RSVPs recorded yet.</div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminEvents;
